import type { Citation } from '../types';

type EventData = {
  citation: Citation;
};

export type CustomCitationEvent = CustomEvent<EventData>;

export type CustomCitationPayload = {
  citations: Citation[];
};

class CustomCitationElement extends HTMLElement {
  private citations: Citation[] = [];

  public dfPayload: string | null = null;
  public dfResponseId: string | null = null;

  constructor() {
    super();

    // Initialize properties which df-cx-messenger will set
    this.dfPayload = null;
    this.dfResponseId = null;

    this.attachShadow({ mode: 'open' });
  }

  /**
   * Called when the element is inserted into the DOM
   */
  connectedCallback() {
    try {
      // Get citation data from the element's payload attribute
      const payloadAttr = this.dfPayload;
      if (payloadAttr) {
        const payload = JSON.parse(payloadAttr) as CustomCitationPayload;
        this.citations = payload.citations || [];
        this.render();
      }
    } catch (error) {
      // console.error('df-custom-citation Error parsing citation payload:', error);
    }
  }

  /**
   * Render the citation UI
   */
  private render() {
    if (!this.shadowRoot) return;

    const style = document.createElement('style');
    style.textContent = `
      :host {
        /* Base styling */
        --df-citation-font-family: inherit;
        --df-citation-font-size-base: 13px;
        --df-citation-font-size-small: 12px;
        --df-citation-font-size-header: 12px;
        
        /* Container styling */
        --df-citation-container-padding: 8px 10px;
        --df-citation-container-margin: 6px 0;
        --df-citation-container-background: #f8f9fa;
        --df-citation-container-border-radius: 10px;
        --df-citation-container-border: 1px solid #e8eaed;
        
        /* Header styling */
        --df-citation-header-color: #5f6368;
        --df-citation-header-margin: 0 0 8px 0;
        --df-citation-header-letter-spacing: 0.5px;
        --df-citation-header-font-weight: 500;
        
        /* List styling */
        --df-citation-list-gap: 6px;
        
        /* Item styling */
        --df-citation-item-padding: 6px 10px;
        --df-citation-item-background: #e8f0fe;
        --df-citation-item-background-hover: #d2e3fc;
        --df-citation-item-border-radius: 16px;
        --df-citation-item-border: 1px solid #d2e3fc;
        --df-citation-item-transition: background-color 0.2s;
        
        /* Title styling */
        --df-citation-title-color: #1967d2;
        --df-citation-title-font-weight: 500;
        --df-citation-title-max-width: 200px;
        
        /* Tooltip styling */
        --df-citation-tooltip-background: #333;
        --df-citation-tooltip-text-color: #fff;
        --df-citation-tooltip-width: 200px;
        --df-citation-tooltip-padding: 8px 10px;
        --df-citation-tooltip-border-radius: 6px;
        --df-citation-tooltip-box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        --df-citation-tooltip-transition-duration: 0.3s;
        --df-citation-tooltip-arrow-size: 5px;
        --df-citation-tooltip-z-index: 10;
      }

      .citation-container {
        padding: var(--df-citation-container-padding);
        margin: var(--df-citation-container-margin);
        background-color: var(--df-citation-container-background);
        border-radius: var(--df-citation-container-border-radius);
        border: var(--df-citation-container-border);
        font-family: var(--df-citation-font-family);
      }
      
      .citation-header {
        font-weight: var(--df-citation-header-font-weight);
        margin: var(--df-citation-header-margin);
        font-size: var(--df-citation-font-size-header);
        color: var(--df-citation-header-color);
        text-transform: uppercase;
        letter-spacing: var(--df-citation-header-letter-spacing);
      }
      
      .citation-list {
        display: flex;
        flex-wrap: wrap;
        gap: var(--df-citation-list-gap);
        position: relative;
        flex: 1 0 auto; /* Allow list to grow and shrink within flex containers */
        box-sizing: border-box; /* Include padding in width calculation */
      }

            
      .citation-item {
        position: relative;
        display: inline-flex;
        align-items: center;
        min-width: 0; /* THE KEY FIX: Allows the item to shrink below its content's width */
        max-width: 100%;
        flex-shrink: 1;
        padding: var(--df-citation-item-padding, 6px 10px);
        background-color: var(--df-citation-item-background, #e8f0fe);
        border-radius: var(--df-citation-item-border-radius, 16px);
        border: var(--df-citation-item-border, 1px solid #d2e3fc);
        cursor: pointer;
        transition: background-color var(--df-citation-item-transition, 0.2s);
      }
            
      .citation-item:hover {
        background-color: var(--df-citation-item-background-hover);
      }
      
      .citation-title {
        font-weight: var(--df-citation-title-font-weight);
        font-size: var(--df-citation-font-size-base);
        color: var(--df-citation-title-color);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: var(--df-citation-title-max-width);
        flex: 0 1 auto; /* Allow title to shrink */
      }
      
      /* W3Schools style tooltip */
      .citation-item .tooltip-text {
        visibility: hidden;
        width: var(--df-citation-tooltip-width);
        background-color: var(--df-citation-tooltip-background);
        color: var(--df-citation-tooltip-text-color);
        text-align: left;
        border-radius: var(--df-citation-tooltip-border-radius);
        padding: var(--df-citation-tooltip-padding);
        position: absolute;
        z-index: var(--df-citation-tooltip-z-index);
        bottom: 125%;
        left: 50%;
        margin-left: calc(var(--df-citation-tooltip-width) / -2);
        opacity: 0;
        transition: opacity var(--df-citation-tooltip-transition-duration), visibility var(--df-citation-tooltip-transition-duration);
        font-size: var(--df-citation-font-size-small);
        box-shadow: var(--df-citation-tooltip-box-shadow);
        pointer-events: none;
      }
      
      /* Arrow for tooltip */
      .citation-item .tooltip-text::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        margin-left: calc(var(--df-citation-tooltip-arrow-size) * -1);
        border-width: var(--df-citation-tooltip-arrow-size);
        border-style: solid;
        border-color: var(--df-citation-tooltip-background) transparent transparent transparent;
      }
      
      /* Show tooltip on hover */
      .citation-item:hover .tooltip-text {
        visibility: visible;
        opacity: 1;
      }
      
      /* Ensure tooltip stays in view */
      .citation-item:first-child .tooltip-text {
        left: 0;
        margin-left: 0;
      }
      
      .citation-item:first-child .tooltip-text::after {
        left: 15px;
        margin-left: 0;
      }
      
      .citation-item:last-child .tooltip-text {
        left: auto;
        right: 0;
        margin-left: 0;
      }
      
      .citation-item:last-child .tooltip-text::after {
        left: auto;
        right: 15px;
        margin-left: 0;
      }
    `;

    const container = document.createElement('div');
    container.className = 'citation-container';

    // Add header
    const header = document.createElement('div');
    header.className = 'citation-header';
    header.textContent = 'Sources';
    container.appendChild(header);

    // Create flex container for citations
    const citationList = document.createElement('div');
    citationList.className = 'citation-list';
    container.appendChild(citationList);

    // Add citations as chips
    this.citations.forEach((citation, index) => {
      const item = document.createElement('div');
      item.className = 'citation-item';
      item.setAttribute('data-index', index.toString());

      // Add citation title
      const title = document.createElement('div');
      title.className = 'citation-title';
      title.textContent = citation.title || 'Source';
      item.appendChild(title);

      // Store original URL if available
      if (citation.anchor?.href) {
        item.setAttribute('data-url', citation.anchor.href);
      }

      // Add subtitle as tooltip if available
      if (citation.subtitle) {
        const tooltip = document.createElement('div');
        tooltip.className = 'tooltip-text';

        // Truncate subtitle if it's too long (more than 100 characters)
        const maxLength = 60;
        tooltip.textContent =
          citation.subtitle.length > maxLength ? `${citation.subtitle.substring(0, maxLength)}...` : citation.subtitle;

        item.appendChild(tooltip);
      }

      // Add click handler
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleCitationClick(citation, index);
      });

      citationList.appendChild(item);
    });

    // Clear any existing content
    while (this.shadowRoot.firstChild) {
      this.shadowRoot.removeChild(this.shadowRoot.firstChild);
    }

    // Add the style and container to the shadow root
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(container);
  }

  /**
   * Handle citation click events
   */
  private handleCitationClick(citation: Citation, index: number) {
    // Dispatch a custom event with the citation data
    const event = new CustomEvent<EventData>('df-custom-citation-clicked', {
      detail: {
        citation,
      },
      bubbles: true,
      composed: true, // This allows the event to cross shadow DOM boundaries
    });

    this.dispatchEvent(event);
  }
}

// Register the custom element
if (!customElements.get('df-custom-citation')) {
  customElements.define('df-custom-citation', CustomCitationElement);
}
